/**
 * File Name          : mcumgr-cap.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.0.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_BACKGROUNDTECH_LICENSE.txt', which is part of this source code package.
 */


// MIT License

// Copyright (c) 2022 Andras Barthazi

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

// Opcodes
const MGMT_OP_READ = 0;
const MGMT_OP_READ_RSP = 1;
const MGMT_OP_WRITE = 2;
const MGMT_OP_WRITE_RSP = 3;

// Groups
const MGMT_GROUP_ID_OS = 0;
const MGMT_GROUP_ID_IMAGE = 1;
const MGMT_GROUP_ID_STAT = 2;
const MGMT_GROUP_ID_CONFIG = 3;
const MGMT_GROUP_ID_LOG = 4;
const MGMT_GROUP_ID_CRASH = 5;
const MGMT_GROUP_ID_SPLIT = 6;
const MGMT_GROUP_ID_RUN = 7;
const MGMT_GROUP_ID_FS = 8;
const MGMT_GROUP_ID_SHELL = 9;

// OS group
const OS_MGMT_ID_ECHO = 0;
const OS_MGMT_ID_CONS_ECHO_CTRL = 1;
const OS_MGMT_ID_TASKSTAT = 2;
const OS_MGMT_ID_MPSTAT = 3;
const OS_MGMT_ID_DATETIME_STR = 4;
const OS_MGMT_ID_RESET = 5;

// Image group
const IMG_MGMT_ID_STATE = 0;
const IMG_MGMT_ID_UPLOAD = 1;
const IMG_MGMT_ID_FILE = 2;
const IMG_MGMT_ID_CORELIST = 3;
const IMG_MGMT_ID_CORELOAD = 4;
const IMG_MGMT_ID_ERASE = 5;

import { BleCharacteristic, BleClient, BleDevice, BleService, dataViewToNumbers, numbersToDataView } from '@capacitor-community/bluetooth-le';
import { encode, decode} from 'cbor-x';
import { HexStringUtils } from './hex-string-utils';
import { PhysensDevice, Device_State } from '../models/physens-devices/physens-device.model';
import { MCU_IMAGE_STATE } from '../models/util/MCU.model';

export class McumgrCap {
  static SERVICE_UUID = '8d53dc1d-1db7-4cd3-868b-8a527460aa84';
  static CHARACTERISTIC_UUID = 'da2e7828-fbce-4e01-ae9e-261174997c48';
  _mtu = 400;
  _device: PhysensDevice | null = null
  _messageCallback: (()=>void) | null = null;
  _imageUploadProgressCallback: ((status: {percentage: number, timeoutAdjusted?: boolean, newTimeout?:number})=>void) | null = null;
  _imageUploadFinishedCallback: (()=>void) | null = null;
  _uploadIsInProgress = false;
  _buffer = new Uint8Array();
  _logger = { info: console.log, error: console.error };
  _seq = 0;
  _userRequestedDisconnect = false;

  _uploadOffset = 0;
  _uploadImage: ArrayBuffer|null = null;
  _uploadSlot = 0

  _consecutiveTimeouts = 0;
  _chunkTimeout = 5000;
  _maxConsecutiveTimeouts = 2;
  // Hard ceiling on total chunk-timeout retries for a single cmdUpload() call. Without this,
  // a mid-upload link loss retries forever (chunk timeout only doubles up to a 15s ceiling,
  // it never gives up), leaving the caller's `await cmdUpload()` hung indefinitely. Past this
  // many consecutive timeouts, reject instead of retrying so the caller's existing
  // failure-handling UI can take over.
  _maxUploadTimeouts = 10;

  // Per-upload OTA diagnostics (bug 20265): chunk send/ack timing and retry counts.
  _lastSendAt = 0;
  _uploadChunks = 0;
  _uploadRetries = 0;
  _uploadAckTotalMs = 0;
  _uploadAckMaxMs = 0;

  uploadPercentage: number = 0;

  _uploadTimeout: ReturnType<typeof setTimeout>|null = null

  _makeKey = (a: number, b: number, c: number) => {
    return `${a},${b},${c}`;
  }

  constructor(device: PhysensDevice) {
    this._device = device
  }

  remove() {
    if (this._uploadTimeout) {
          clearTimeout(this._uploadTimeout);
    }
  }

  // Sticky hard-stop (bug 20264: device placed on charger mid-OTA). Once set, this instance
  // never writes again — the chunk-retry timer is cleared, the in-flight upload and every
  // pending command are rejected, and _sendMessage() refuses to write. Deliberately never
  // reset: an aborted device is torn down, and a retry runs on a freshly paired instance.
  _aborted = false;
  abort(reason: string) {
    this._aborted = true;
    this.remove();
    this._uploadTimeout = null;
    this._uploadImage = null;
    if (this._uploadIsInProgress) {
      this._uploadIsInProgress = false;
      this._imageUploadPromiseReject?.(new Error(reason));
    }
    this.clearPendingResolvers(reason);
  }

    pendingResolvers = new Map<string, [(value: unknown) => void, (reason?: any) => void]>();
    _imageUploadPromiseResolve: (()=> void)|null = null;
    _imageUploadPromiseReject: ((reason?: any) => void)|null = null;

    // Called on GATT disconnect. A write's response is awaited via pendingResolvers
    // with no timeout of its own - if the device drops before the matching notification
    // arrives (e.g. mid-command link loss), that promise would otherwise hang forever,
    // and since every mcumgr command also holds the device-level write mutex
    // (_device.writePromise) until it settles, a single stranded command permanently
    // wedges every future BLE write for the rest of the session. Force-reject
    // everything in flight so callers (mcumgrImageStateWithRetry, the confirm-poll
    // loops) see a real rejection and their own retry/backoff logic can take over.
    clearPendingResolvers(reason: string) {
      for(const [, reject] of this.pendingResolvers.values()) {
        reject(new Error(reason));
      }
      this.pendingResolvers.clear();
    }


  // Waits (bounded) for the device to actually be Ready for Packets before issuing a
  // write. getPrimaryService/writeWithoutResponse against a GATT server torn down by a
  // just-completed reboot silently hangs rather than rejecting, so a write fired the
  // instant a caller thinks it's ready (e.g. right after reconnect) can wedge the whole
  // update. Callers (mcumgrImageStateWithRetry, the confirm-poll loops) already retry
  // with backoff on a thrown error, so failing fast here composes cleanly with them.
  private async _waitUntilReadyForWrite(timeoutMs = 3000): Promise<void> {
    const device = this._device;
    if(!device) throw new Error('mcumgr write skipped: device unavailable');
    if(device.deviceState === Device_State.READY_FOR_PACKETS) return;
    console.log('[FWUpdate] mcumgr write deferred - device not Ready for Packets, waiting up to', timeoutMs, 'ms');
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const sub = device.deviceStateSubject.subscribe((state) => {
        if(settled) return;
        if(state === Device_State.READY_FOR_PACKETS) {
          settled = true;
          clearTimeout(timer);
          sub.unsubscribe();
          resolve();
        }
      });
      const timer = setTimeout(() => {
        if(settled) return;
        settled = true;
        sub.unsubscribe();
        reject(new Error('mcumgr write skipped: device not Ready for Packets after ' + timeoutMs + 'ms'));
      }, timeoutMs);
    });
    console.log('[FWUpdate] mcumgr write proceeding - device is now Ready for Packets');
  }

  async _sendMessage(op: number, group: number, id: number, data?: MCUmgrMessage): Promise<unknown> {
      if(!this._device) return;
      if(this._aborted) throw new Error('mcumgr write skipped: update aborted');
      await this._waitUntilReadyForWrite();
      const _flags = 0;
      let encodedData: number[] = [];
      if (typeof data !== 'undefined') {
          encodedData = [...new Uint8Array(encode(data))];
      }
      const length_lo = encodedData.length & 255;
      const length_hi = encodedData.length >> 8;
      const group_lo = group & 255;
      const group_hi = group >> 8;
      const message = [op, _flags, length_hi, length_lo, group_hi, group_lo, this._seq, id, ...encodedData];
      let promise = new Promise<unknown>((resolve, reject)=> {
        this.pendingResolvers.set(this._makeKey(op, group, id), [resolve, reject])
      })
    //   console.log('mcu-mgr >'  + message.map(x => x.toString(16).padStart(2, '0')).join(' '));
      var dv = new DataView(new Uint8Array(message).buffer)
      // Web Bluetooth serializes ALL GATT ops on a connection, so an mcumgr
      // write (this characteristic) will collide with a concurrent NUS write
      // from the housekeeping drain and throw "GATT operation already in
      // progress", corrupting mid-upload chunks. Acquire the device-level
      // BLE write mutex and hold it for the duration of this write.
      await this._device.acquireBleWriteSlot();
      // Re-check after the awaits above: an abort can land while this write was queued.
      if(this._aborted) {
        this.pendingResolvers.delete(this._makeKey(op, group, id));
        throw new Error('mcumgr write skipped: update aborted');
      }
      let writeDone: () => void = () => {};
      let writeFail: (e: any) => void = () => {};
      this._device.writePromise = new Promise<void>((res, rej) => {
        writeDone = res;
        writeFail = rej;
      });
      try {
        await BleClient.writeWithoutResponse(HexStringUtils.hexToString(this._device.deviceId), McumgrCap.SERVICE_UUID, McumgrCap.CHARACTERISTIC_UUID, dv)
        writeDone();
        this._device.writePromise = null;
      } catch (e) {
        // Write rejected (e.g. stale GATT handle, MTU mismatch). Clean up the pending
        // resolver so a late/unrelated notification can't resolve the wrong promise,
        // and reject it (instead of leaving it hanging forever) so callers can react.
        const pending = this.pendingResolvers.get(this._makeKey(op, group, id));
        this.pendingResolvers.delete(this._makeKey(op, group, id));
        pending?.[1](e);
        writeFail(e);
        this._device.writePromise = null;
        throw e;
      }
      this._seq = (this._seq + 1) % 256;
      return promise;
  }
  _notification(data: DataView) {
      // console.log('message received');
      const message = new Uint8Array(data.buffer);
      // console.log(message);
      // console.log('<'  + [...message].map(x => x.toString(16).padStart(2, '0')).join(' '));
      this._buffer = new Uint8Array([...this._buffer, ...message]);
      const messageLength = this._buffer[2] * 256 + this._buffer[3];
      if (this._buffer.length < messageLength + 8) return;
      this._processMessage(this._buffer.slice(0, messageLength + 8));
      this._buffer = this._buffer.slice(messageLength + 8);
  }
  _processMessage(message: Uint8Array) {
      const [op, _flags, length_hi, length_lo, group_hi, group_lo, _seq, id] = message;
      const data = decode(message.slice(8));
      const length = length_hi * 256 + length_lo;
      const group = group_hi * 256 + group_lo;
      const resolver = this.pendingResolvers.get(this._makeKey(op-1, group, id))
      if(resolver) {
        resolver[0](data)
        this.pendingResolvers.delete(this._makeKey(op-1, group, id))
      }

    //   console.log('[MCUManager DEBUG] Message received:', {
    //         op,
    //         group,
    //         id,
    //         length,
    //         dataKeys: data ? Object.keys(data) : 'null',
    //         data: data
    //     });

      if (group === MGMT_GROUP_ID_IMAGE && id === IMG_MGMT_ID_UPLOAD) {
        if(data.rc && data.rc !== 0) {
            //error response
        }
        if ((data.rc === 0 || data.rc === undefined) && data.off){
            const ackMs = Math.round(performance.now() - this._lastSendAt);
            this._uploadChunks++;
            this._uploadAckTotalMs += ackMs;
            this._uploadAckMaxMs = Math.max(this._uploadAckMaxMs, ackMs);
            console.log(`[FWUpdate][OTA] chunk acked off=${this._uploadOffset}->${data.off} sendToAck=${ackMs}ms`);
            this._consecutiveTimeouts = 0;
            // Clear timeout since we received a response
            if (this._uploadTimeout) {
                clearTimeout(this._uploadTimeout);
            }
            this._uploadOffset = data.off;            
            this._uploadNext();
            return;
        }
      } 
      // if (this._messageCallback) this._messageCallback({ op, group, id, data, length });
  }
  cmdReset() {
      return this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_OS, OS_MGMT_ID_RESET);
  }
  smpEcho(message: any) {
      return this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_OS, OS_MGMT_ID_ECHO, { d: message });
  }
  cmdImageState() {
      return this._sendMessage(MGMT_OP_READ, MGMT_GROUP_ID_IMAGE, IMG_MGMT_ID_STATE) as Promise<{images: MCU_IMAGE_STATE[], splitstatus: number}>;
  }
  cmdImageErase() {
      return this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_IMAGE, IMG_MGMT_ID_ERASE, {});
  }
  cmdImageTest(hash: ArrayBuffer) {
      return this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_IMAGE, IMG_MGMT_ID_STATE, { hash, confirm: false });
  }
  cmdImageConfirm(hash: ArrayBuffer) {
      return this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_IMAGE, IMG_MGMT_ID_STATE, { hash, confirm: true });
  }
  _hash(image: ArrayBuffer) {
      return crypto.subtle.digest('SHA-256', image);
  }
  async _uploadNext() {
      if(!this._uploadImage || this._aborted) return;

      if (this._uploadOffset >= this._uploadImage.byteLength) {
          this._uploadIsInProgress = false;
          this.uploadPercentage = 100;
          this._logUploadSummary('done');
          if(this._imageUploadPromiseResolve != undefined) this._imageUploadPromiseResolve();
          return;
      }

      // Clear any existing timeout
      if (this._uploadTimeout) {
          clearTimeout(this._uploadTimeout);
      }
      // Set new timeout
      this._uploadTimeout = setTimeout(() => {
            this._consecutiveTimeouts++;
          this._uploadRetries++;
          this._logger.info(`Upload chunk timeout, retry off=${this._uploadOffset} timeout=${this._chunkTimeout}ms consecutive=${this._consecutiveTimeouts}`);

          if (this._consecutiveTimeouts >= this._maxUploadTimeouts) {
            this._logUploadSummary('gave up');
            this._logger.error(`Upload chunk timed out ${this._consecutiveTimeouts} times in a row - giving up`);
            this._uploadIsInProgress = false;
            if (this._imageUploadPromiseReject != undefined) {
              this._imageUploadPromiseReject(new Error('Upload stalled: device unreachable after repeated retries'));
            }
            return;
          }

          if (this._consecutiveTimeouts >= this._maxConsecutiveTimeouts) {
            this._chunkTimeout = Math.min(this._chunkTimeout * 2, 15000); // Max 15 seconds
            this._logger.info(`Increased chunk timeout to ${this._chunkTimeout}ms`);
            // Notify UI about timeout adjustment
            if (this._imageUploadProgressCallback) {
                this._imageUploadProgressCallback({
                    percentage: Math.floor(this._uploadOffset / this._uploadImage!.byteLength * 100),
                    timeoutAdjusted: true,
                    newTimeout: this._chunkTimeout
                });
            }
        }

          this._uploadNext();
      }, this._chunkTimeout);

      const nmpOverhead = 8;
      const message: MCUmgrMessage = { data: new Uint8Array(), off: this._uploadOffset };
      if (this._uploadOffset === 0) {
          message.len = this._uploadImage.byteLength;
          message.sha = new Uint8Array(await this._hash(this._uploadImage));
      }
      if(this._imageUploadProgressCallback) this._imageUploadProgressCallback({ percentage: Math.floor(this._uploadOffset / this._uploadImage.byteLength * 100) });

      // Logged once per percent, not per chunk: ~13 identical lines per percent read as retries (bug 20265).
      const percentage = Math.floor(this._uploadOffset / this._uploadImage.byteLength * 100);
      if (percentage !== this.uploadPercentage || this._uploadOffset === 0) console.log("upload percentage " + percentage);
      this.uploadPercentage = percentage;
      const length = this._mtu - encode(message).byteLength - nmpOverhead;

      message.data = new Uint8Array(this._uploadImage.slice(this._uploadOffset, this._uploadOffset + length));

      // Keep offset for retry
      // this._uploadOffset += length;

      this._lastSendAt = performance.now();
      console.log(`[FWUpdate][OTA] chunk send off=${this._uploadOffset} len=${length} t=${Math.round(this._lastSendAt)}ms`);
      this._sendMessage(MGMT_OP_WRITE, MGMT_GROUP_ID_IMAGE, IMG_MGMT_ID_UPLOAD, message).catch((e) => {
        // _uploadOffset was not advanced, so the existing _uploadTimeout will retry this
        // same chunk when it fires. Just surface the failure instead of leaving an
        // unhandled promise rejection.
        this._logger.error('Upload chunk send failed, will retry on timeout: ' + String(e));
      });
  }
  _logUploadSummary(outcome: string) {
      const avg = this._uploadChunks ? Math.round(this._uploadAckTotalMs / this._uploadChunks) : 0;
      console.log(`[FWUpdate][OTA] upload ${outcome}: chunks=${this._uploadChunks} retries=${this._uploadRetries} ackAvg=${avg}ms ackMax=${this._uploadAckMaxMs}ms finalTimeout=${this._chunkTimeout}ms`);
  }
  async cmdUpload(image: ArrayBuffer, slot = 0) {
      if (this._uploadIsInProgress) {
          this._logger.error('Upload is already in progress.');
          return;
      }
      this._uploadIsInProgress = true;

      this._uploadChunks = this._uploadRetries = this._uploadAckTotalMs = this._uploadAckMaxMs = 0;
      this._uploadOffset = 0;
      this._uploadImage = image;
      this._uploadSlot = slot;
      this.uploadPercentage = 0;
      this._uploadNext();
      let promise = new Promise<void>((resolve, reject)=> {
        this._imageUploadPromiseResolve = resolve;
        this._imageUploadPromiseReject = reject;
      })
      return promise;
  }
  async imageInfo(image: ArrayBuffer): Promise<MCUmgrImageInfo> {
      // https://interrupt.memfault.com/blog/mcuboot-overview#mcuboot-image-binaries

      const info: MCUmgrImageInfo = {};
    //   const view = new Uint8Array(image);

    //   const info = {};
        info.tags = {};
        const view = new DataView(image);

        // check header length
        if (view.byteLength < 32) {
            throw new Error('Invalid image (too short file)');
        }

        // check MAGIC bytes 0x96f3b83d
        if (view.getUint32(0, true) !== 0x96f3b83d) {
            throw new Error('Invalid image (wrong magic bytes)');
        }

        // check load address is 0x00000000
        if (view.getUint32(4, true) !== 0) {
            throw new Error('Invalid image (wrong load address)');
        }

        const headerSize = view.getUint16(8, true);

        // Protected TLV area is included in the hash
        const protected_tlv_length = view.getUint16(10, true);

        const imageSize = view.getUint32(12, true);
        info.imageSize = imageSize;

        // check image size is correct
        if (view.byteLength < imageSize + headerSize) {
            throw new Error('Invalid image (wrong image size)');
        }

        // check flags is 0x00000000
        if (view.getUint32(16, true) !== 0x00) {
            // throw new Error('Invalid image (wrong flags)');
        }

        const version = `${view.getUint8(20)}.${view.getUint8(21)}.${view.getUint16(22, true)}`;
        info.version = version;

        const hashBytes = new Uint8Array(await this._hash(image.slice(0, imageSize + headerSize + protected_tlv_length)));
        info.hash = [...hashBytes].map(b => b.toString(16).padStart(2, '0')).join('');

        let offset = headerSize + imageSize;
        let tlv_end = offset;

        // Only if it was indicated that there were protected TLVs
        if (protected_tlv_length > 0) {
            // Verify the protected TLV magic bytes are valid.
            if (view.getUint16(offset, true) !== 0x6908) {
                throw new Error( `Expected protected TLV magic number. (0x${offset.toString(16)}: 0x${view.getUint16(offset, true).toString(16)})`);
            }

            // Find the end of the protected TLV region
            tlv_end = view.getUint16(offset + 2, true) + offset;
            // Store all tag-value pairs for the protected TLV region.
            for (let tlv of this._extractTlvs(view.buffer.slice(offset + 4, tlv_end))) {
                info.tags[tlv.tag] = tlv.value;
            }
            offset = tlv_end;
        }

        // The non-protected TLV region must be here.
        if (view.getUint16(offset, true) !== 0x6907) {
            throw new Error(`Expected TLV magic number. (0x${offset.toString(16)}: 0x${view.getUint16(offset, true).toString(16)})`);
        }

        // Also include the non-protected TLVs in the tags map.
        // Assume there are no overlapping tag Ids.
        tlv_end = view.getUint16(offset + 2, true) + offset;
        for (let tlv of this._extractTlvs(view.buffer.slice(offset + 4, tlv_end))) {
            info.tags[tlv.tag] = tlv.value;
        }

        // If the image hash tag is present, verify it matches what was calculated earlier.
        if (16 in info.tags && info.tags[16].length == hashBytes.length) {
            info.hashValid = info.tags[16].every((b, i) => b === hashBytes[i]);
        }

        return info;
  }
*_extractTlvs(data: ArrayBuffer): Generator<{ tag: number; value: Uint8Array }> {
    const view = new DataView(data);
    let offset = 0;
    while (offset < view.byteLength) {
        const tag = view.getUint16(offset, true);
        const len = view.getUint16(offset + 2, true);
        offset += 4;
        const valueData = view.buffer.slice(offset, offset + len);
        offset += len;

        yield { tag, value: new Uint8Array(valueData) };
    }
}
}

export interface MCUmgrImageInfo {
  imageSize?: number,
  version?: string,
  hash?: string

  hashValid?: boolean,
tags?: { [key: number]: Uint8Array }    
}

interface MCUmgrMessage {
  hash?: ArrayBuffer,
  confirm?: boolean,
  d?: any,
  data?: Uint8Array,
  off?: number,
  len?: number,
  sha?: Uint8Array
}